import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCampaignLayerContent, useGetPlaylistLayerContent } from '../../../../../../queries';
import { Spinner } from 'react-bootstrap';
import { MediaPlayer } from '../../../../../Devices/Component/Preview/Custom/MediaPlayer';
import { useDispatch } from 'react-redux';
import { setPlaybackStatus } from '../../../../../../redux/Slices/deviceSlice';

const ProgressBar = ({ duration, isActive, isCompleted, manualProgress }) => {
    const [width, setWidth] = useState(isCompleted ? 100 : 0);

    useEffect(() => {
        if (manualProgress !== null && manualProgress !== undefined) {
             return;
        }

        if (isActive) {
            setWidth(0);
             const timer = setTimeout(() => {
                setWidth(100);
            }, 50);
            return () => clearTimeout(timer);
        } else if (isCompleted) {
            setWidth(100);
        } else {
            setWidth(0);
        }
    }, [isActive, isCompleted, duration, manualProgress]);

    const displayWidth = (manualProgress !== null && manualProgress !== undefined) ? manualProgress : width;
    
    return (
        <div className="flex-1 bg-gray-500/50 rounded-full h-1 overflow-hidden shadow-sm mx-0.5">
            <div
                className="h-full bg-white shadow-sm"
                style={{
                    width: `${displayWidth}%`,
                    transition: (manualProgress !== null && manualProgress !== undefined) 
                        ? 'width 0.1s linear'
                        : (isActive ? `width ${duration}s linear` : 'none')
                }}
            />
        </div>
    );
};

const VisualAreaContent = ({ areaId, settings, showTimeline = true, isPaused = false, forcedItem = null, onItemChange, objectFitMode }) => {
    const { t } = useTranslation();
    const { data: rawData, isLoading } = useGetCampaignLayerContent(areaId, "MAIN");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [resetKey, setResetKey] = useState(0);
    const [isVisible, setIsVisible] = useState(true); // Start as visible to load immediately
    const [isContentLoading, setIsContentLoading] = useState(true);
    const containerRef = useRef(null);
    const dispatch = useDispatch();

    // Detect playlists in rawData
    const playlistId = useMemo(() => {
        if (!rawData || rawData.length === 0) return null;
        const playlistItem = rawData.find(item => item.type === "EVENT" && item.playlist_id);
        return playlistItem?.playlist_id || null;
    }, [rawData]);

    // Fetch playlist content if detected
    const { data: playlistMediaData, isLoading: playlistLoading } = useGetPlaylistLayerContent(
        playlistId, 
        "MAIN"
    );

    // Expand playlists into media items
    const data = useMemo(() => {
        if (!rawData) return null;
        
        const expanded = [];
        rawData.forEach(item => {
            if (item.type === "EVENT" && item.playlist_id) {
                if (playlistMediaData && playlistMediaData.length > 0) {
                    playlistMediaData.forEach(mediaItem => {
                        expanded.push({
                            ...mediaItem,
                            is_muted: item.is_muted,
                            maintain_aspect_ratio: item.maintain_aspect_ratio,
                        });
                    });
                }
            } else {
                expanded.push(item);
            }
        });
        
        if (expanded.length === 0) return null;

        // Assign stable instance IDs based on final flat list index
        return expanded.map((item, idx) => ({
             ...item,
             _instanceId: `${item.id || item.media_id}_${areaId}_${idx}`
        }));
    }, [rawData, playlistMediaData, areaId]);

    // Reset to first item when data changes
    useEffect(() => {
        setCurrentIndex(0);
        setElapsedSeconds(0);
        setCurrentProgress(0);
        setResetKey(prev => prev + 1);
        setIsContentLoading(true); // Reset loading state when data changes
    }, [data]);

    // Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { 
                threshold: 0.1, // Load when 10% visible
                rootMargin: '50px' // Start loading slightly before visible
            }
        );
        
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        
        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);

    // Handle jumping to a specific item from outside (e.g. sidebar selection)
    useEffect(() => {
        if (!forcedItem || !data) return;
        
        const index = data.findIndex(item => item.id === forcedItem.id || item._instanceId === forcedItem._instanceId);
        
        // If it's the same item and NOT a user-forced selection (no timestamp), ignore to prevent reset loop
        if (index === currentIndex && !forcedItem._forceSelect) {
            return;
        }

        if (index !== -1) {
            setCurrentIndex(index);
            setElapsedSeconds(0);
            setCurrentProgress(0);
            setResetKey(prev => prev + 1);
        }
    }, [forcedItem, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = () => {
        setCurrentIndex((prevIndex) => {
            const nextIndex = (prevIndex + 1) % data.length;
            setElapsedSeconds(0); 
            setCurrentProgress(0);
            setResetKey(prev => prev + 1);
            return nextIndex;
        });
    };

    // Dispatch status update whenever currentIndex changes
    useEffect(() => {
        if (!data || !data[currentIndex]) return;
        const currentItem = data[currentIndex];
        const duration = parseFloat(currentItem?.custom_duration) || 10;
        
        // Notify parent about item change (for sidebar highlight)
        if (onItemChange) {
            onItemChange(currentItem);
        }

        dispatch(setPlaybackStatus({
            id: areaId,
            currentItemId: currentItem.id,
            totalDuration: duration,
            progress: 0
        }));
    }, [currentIndex, data, areaId, dispatch]);

    const handleProgress = (progress) => {
        setCurrentProgress(progress);
        dispatch(setPlaybackStatus({
            id: areaId,
            progress: progress
        }));
    };

    // Auto-rotate through content items based on duration
    useEffect(() => {
        if (!data || data.length === 0) return;
        if (isPaused) return;

        const currentItem = data[currentIndex];
        const path = currentItem?.path || '';
        
        const isVideo = (currentItem?.media_type === "VIDEO" || 
                        (!currentItem?.media_type && /\.(mp4|webm|ogg|mov)$/i.test(path))) && 
                        !path.toLowerCase().endsWith(".avi");

        // If it's a video, rely on onEnded event (no timer)
        if (isVideo) {
            return;
        }

        const duration = parseFloat(currentItem?.custom_duration) || 10;
        
        // Set up timer to move to next content after duration
        const timer = setTimeout(handleNext, duration * 1000);

        return () => clearTimeout(timer);
    }, [data, currentIndex, isPaused]);

    if (isLoading || (playlistId && playlistLoading)) return (
        <div className="absolute inset-0 flex justify-center items-center z-0 opacity-50 bg-gray-100">
             <Spinner animation="border" size="sm" />
        </div>
    );
    
    if (!data || data.length === 0) return null;

    const { is_muted, maintain_aspect_ratio, ...otherSettings } = settings || {};
    
    const currentItem = {
        ...data[currentIndex],
        ...otherSettings 
    };
    
    const isCurrentVideo = (currentItem?.media_type === "VIDEO" || 
                           (!currentItem?.media_type && /\.(mp4|webm|ogg|mov)$/i.test(currentItem?.path || ''))) && 
                           !currentItem?.path?.toLowerCase().endsWith(".avi");


    return (
        <div ref={containerRef} className="absolute inset-0 z-0 w-full h-full pointer-events-none overflow-hidden">
             {data.length > 1 && showTimeline && (
                <div className="absolute top-4 left-2 right-2 flex z-50">
                    {data.map((item, index) => {
                        const isMetaVideo = (item?.media_type === "VIDEO" || 
                                           (!item?.media_type && /\.(mp4|webm|ogg|mov)$/i.test(item?.path || ''))) && 
                                           !item?.path?.toLowerCase().endsWith(".avi");
                        
                        const shouldUseManual = (index === currentIndex) && isMetaVideo;

                        return (
                            <ProgressBar 
                                key={index}
                                duration={parseFloat(item?.custom_duration) || 10}
                                isActive={index === currentIndex && !isPaused}
                                isCompleted={index < currentIndex}
                                manualProgress={shouldUseManual ? currentProgress : null}
                            />
                        );
                    })}
                </div>
            )}
             <MediaPlayer 
                key={`${currentItem.id}-${currentIndex}-${resetKey}`}
                data={currentItem} 
                elapsedSeconds={elapsedSeconds} 
                customDuration={parseFloat(currentItem?.custom_duration) || 0} 
                onEnded={handleNext}
                onProgress={handleProgress}
                areaId={areaId}
                forcePause={isPaused}
                isVisible={isVisible}
                preloadStrategy="metadata"
                onLoadStart={() => setIsContentLoading(true)}
                onCanPlay={() => setIsContentLoading(false)}
                objectFitMode={objectFitMode}
                cleanWrapper={true}
             />
             
             {/* Loading Indicator */}
             {isContentLoading && isVisible && (
                 <div className="absolute inset-0 flex flex-col justify-center items-center bg-gray-900 bg-opacity-40 backdrop-blur-sm z-30">
                     <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                     <span className="text-white text-xs font-medium">{t('layouts.designer_loading')}</span>
                 </div>
             )}
             {data.length > 1 && (
                <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-[10px] px-1 rounded z-20">
                    {currentIndex + 1}/{data.length}
                </div>
            )}
        </div>
    );
};

export default VisualAreaContent;
